// Body Map content: ten organs, where they go on the outline (fractions of the body box),
// what system they belong to, what they need, and one fact each.

export type SystemId = 'nervous' | 'circulatory' | 'respiratory' | 'digestive' | 'skeletal' | 'muscular' | 'skin' | 'excretory';

export interface Organ {
  id: string; name: string; color: string;
  x: number; y: number;          // target center, fractions of the body box
  w: number; h: number;          // drawn size, fractions of the body box width
  system: SystemId;
  need: string;
  fact: string;
  shape: 'brain' | 'heart' | 'lungs' | 'stomach' | 'intestines' | 'liver' | 'kidneys' | 'bone' | 'muscle' | 'skin';
}

export const ORGANS: Organ[] = [
  { id: 'brain', name: 'Brain', color: '#ee86b7', x: 0.5, y: 0.075, w: 0.22, h: 0.18, system: 'nervous', need: 'oxygen and sleep', fact: 'Your brain uses about a fifth of all the energy you eat.', shape: 'brain' },
  { id: 'lungs', name: 'Lungs', color: '#f4b8c8', x: 0.5, y: 0.31, w: 0.34, h: 0.2, system: 'respiratory', need: 'air', fact: 'Spread flat, your lungs would cover a tennis court.', shape: 'lungs' },
  { id: 'heart', name: 'Heart', color: '#e2574c', x: 0.46, y: 0.33, w: 0.13, h: 0.12, system: 'circulatory', need: 'exercise', fact: 'Your heart beats about 100,000 times a day.', shape: 'heart' },
  { id: 'liver', name: 'Liver', color: '#a0522d', x: 0.42, y: 0.46, w: 0.2, h: 0.1, system: 'digestive', need: 'water', fact: 'The liver cleans your blood and can regrow if part is removed.', shape: 'liver' },
  { id: 'stomach', name: 'Stomach', color: '#f08a3c', x: 0.57, y: 0.47, w: 0.15, h: 0.11, system: 'digestive', need: 'food', fact: 'Stomach acid is strong enough to dissolve metal.', shape: 'stomach' },
  { id: 'kidneys', name: 'Kidneys', color: '#8e3b46', x: 0.5, y: 0.53, w: 0.3, h: 0.08, system: 'excretory', need: 'water', fact: 'Your two kidneys filter all your blood about 40 times a day.', shape: 'kidneys' },
  { id: 'intestines', name: 'Intestines', color: '#f5c542', x: 0.5, y: 0.62, w: 0.3, h: 0.15, system: 'digestive', need: 'fiber', fact: 'Uncoiled, your small intestine is about as long as a bus.', shape: 'intestines' },
  { id: 'bone', name: 'Bone (femur)', color: '#f6f3ec', x: 0.41, y: 0.82, w: 0.07, h: 0.26, system: 'skeletal', need: 'calcium', fact: 'The femur in your thigh is the longest and strongest bone you have.', shape: 'bone' },
  { id: 'muscle', name: 'Muscle (bicep)', color: '#c0392b', x: 0.2, y: 0.33, w: 0.08, h: 0.16, system: 'muscular', need: 'exercise', fact: 'You have more than 600 muscles, and even smiling uses about 12.', shape: 'muscle' },
  { id: 'skin', name: 'Skin', color: '#e9b995', x: 0.8, y: 0.42, w: 0.1, h: 0.12, system: 'skin', need: 'sunscreen and water', fact: 'Skin is your biggest organ. You shed and regrow it about once a month.', shape: 'skin' },
];

export const ORGAN_BY_ID: Record<string, Organ> = Object.fromEntries(ORGANS.map(o => [o.id, o]));

/**
 * Slot positions on the painted base (bodymap.base, a child in an anatomy apron, 2:3 box).
 * Measured from the image: fractions of the image box, w and h as fractions of its width.
 */
export const PAINTED_SLOTS: Record<string, { x: number; y: number; w: number; h: number }> = {
  brain: { x: 0.494, y: 0.13, w: 0.24, h: 0.15 },
  lungs: { x: 0.494, y: 0.479, w: 0.27, h: 0.17 },
  heart: { x: 0.498, y: 0.476, w: 0.085, h: 0.095 },
  liver: { x: 0.42, y: 0.553, w: 0.15, h: 0.08 },
  stomach: { x: 0.56, y: 0.558, w: 0.12, h: 0.11 },
  kidneys: { x: 0.49, y: 0.628, w: 0.22, h: 0.065 },
  intestines: { x: 0.488, y: 0.651, w: 0.18, h: 0.13 },
  bone: { x: 0.38, y: 0.8, w: 0.06, h: 0.15 },
  muscle: { x: 0.273, y: 0.469, w: 0.07, h: 0.1 },
  skin: { x: 0.76, y: 0.55, w: 0.08, h: 0.08 },
};

/** Trace hotspots on the painted base. */
export const PAINTED_HOTSPOTS: Record<string, { x: number; y: number }> = {
  nose: { x: 0.494, y: 0.234 },
  mouth: { x: 0.494, y: 0.262 },
  windpipe: { x: 0.498, y: 0.306 },
  blood: { x: 0.2, y: 0.59 },
};

export const SYSTEM_LABEL: Record<SystemId, string> = {
  nervous: 'nervous system', circulatory: 'circulatory system', respiratory: 'breathing system', digestive: 'digestive system',
  skeletal: 'skeleton', muscular: 'muscles', skin: 'skin', excretory: 'cleaning system',
};

export const TIER1_IDS = ['brain', 'heart', 'lungs', 'stomach', 'bone'];
